import { useState, useEffect, useMemo } from 'react'
import { motion } from 'framer-motion'
import { useParams, Link } from 'react-router-dom'
import { Calendar, Clock, ArrowLeft, Share2, Facebook, Twitter, Linkedin } from 'lucide-react'
import { marked } from 'marked'
import SEO from '../components/SEO'

interface BlogPostData {
  id: string
  slug: string
  title: string
  excerpt: string
  content: string
  image: string
  category: string
  author: string
  date: string
  readTime: number
  seo?: {
    metaTitle?: string
    metaDescription?: string
  }
}

export default function BlogPost() {
  const { slug } = useParams<{ slug: string }>()
  const [post, setPost] = useState<BlogPostData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)

  useEffect(() => {
    const fetchPost = async () => {
      if (!slug) return
      
      setLoading(true)
      setError(false)
      
      try {
        const response = await fetch(`/api/posts/${slug}`)
        if (!response.ok) {
          throw new Error('Post not found')
        }
        const data = await response.json()
        setPost(data)
        
        // Track view after successfully loading the post
        if (data.id) {
          trackView(data.id)
        }
      } catch (err) {
        console.error('Error fetching post:', err)
        setError(true)
      } finally {
        setLoading(false)
      }
    }
    
    fetchPost()
  }, [slug])

  // Track view (fire and forget)
  const trackView = async (postId: string) => {
    try {
      await fetch(`/api/analytics/view/${postId}`, {
        method: 'POST',
      })
    } catch (err) {
      // Silently fail - analytics shouldn't break the page
      console.error('Analytics error:', err)
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('it-IT', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    })
  }

  const shareUrl = typeof window !== 'undefined' ? window.location.href : ''

  // Parse content - support both HTML and Markdown
  const parsedContent = useMemo(() => {
    if (!post?.content) return ''
    
    // Check if content is already HTML (starts with < or contains HTML tags)
    const isHTML = post.content.trim().startsWith('<') || /<[a-z][\s\S]*>/i.test(post.content)
    
    if (isHTML) {
      return post.content
    }
    
    // Parse as Markdown
    return marked.parse(post.content, { breaks: true }) as string
  }, [post?.content])

  if (loading) {
    return (
      <div className="pt-24 pb-16">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto animate-pulse">
            <div className="bg-dark-100 h-8 w-32 rounded mb-8" />
            <div className="bg-dark-100 h-12 rounded mb-4" />
            <div className="bg-dark-100 h-6 w-3/4 rounded mb-8" />
            <div className="bg-dark-100 h-64 rounded-2xl mb-8" />
            <div className="space-y-4">
              {[1, 2, 3, 4, 5].map(i => (
                <div key={i} className="bg-dark-100 h-4 rounded" />
              ))}
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (error || !post) {
    return (
      <div className="pt-24 pb-16">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <img
              src="/carletto/9.png"
              alt="Articolo non trovato"
              className="w-48 h-auto mx-auto mb-6"
            />
            <h1 className="heading-md mb-4">Articolo non trovato</h1>
            <p className="text-dark-500 mb-8">
              L'articolo che stai cercando non esiste o è stato rimosso.
            </p>
            <Link to="/il-blog-di-carletto" className="btn-primary">
              Torna al Blog
            </Link>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="pt-24 pb-16">
      {post && (
        <SEO
          title={post.seo?.metaTitle || post.title}
          description={post.seo?.metaDescription || post.excerpt}
          canonical={`/blog/${post.slug}`}
          ogType="article"
          ogImage={post.image}
          article={{
            title: post.title,
            description: post.excerpt,
            image: post.image,
            datePublished: post.date,
            author: post.author,
            category: post.category,
          }}
          breadcrumbs={[
            { name: 'Home', url: '/' },
            { name: 'Blog', url: '/il-blog-di-carletto' },
            { name: post.title, url: `/blog/${post.slug}` },
          ]}
        />
      )}
      <article className="container-custom">
        <div className="max-w-3xl mx-auto">
          {/* Back Link */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
          >
            <Link
              to="/il-blog-di-carletto"
              className="inline-flex items-center gap-2 text-dark-500 hover:text-primary transition-colors mb-8"
            >
              <ArrowLeft className="w-4 h-4" />
              Torna al Blog
            </Link>
          </motion.div>

          {/* Header */}
          <motion.header
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <span className="inline-block px-3 py-1 bg-primary/10 text-primary-700 font-medium rounded-full text-sm mb-4">
              {post.category}
            </span>
            <h1 className="heading-lg mb-4">{post.title}</h1>
            <p className="text-xl text-dark-500 mb-6">{post.excerpt}</p>

            {/* Meta */}
            <div className="flex flex-wrap items-center gap-6 pb-8 border-b border-dark-100">
              <div className="flex items-center gap-3">
                <img
                  src="/carletto/3.png"
                  alt={post.author}
                  className="w-10 h-10 rounded-full bg-primary/10"
                />
                <span className="font-medium text-dark-700">{post.author}</span>
              </div>
              <span className="flex items-center gap-1 text-dark-500">
                <Calendar className="w-4 h-4" />
                {formatDate(post.date)}
              </span>
              <span className="flex items-center gap-1 text-dark-500">
                <Clock className="w-4 h-4" />
                {post.readTime} min di lettura
              </span>
            </div>
          </motion.header>

          {/* Featured Image */}
          {post.image && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="my-8"
            >
              <div className="bg-primary/10 rounded-2xl overflow-hidden flex items-center justify-center p-6">
                <img
                  src={post.image}
                  alt={post.title}
                  className="w-full h-auto max-h-80 object-contain"
                />
              </div>
            </motion.div>
          )}

          {/* Content */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="prose prose-lg max-w-none mb-12"
            dangerouslySetInnerHTML={{ __html: parsedContent }}
          />

          {/* Share */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="border-t border-b border-dark-100 py-6 mb-12"
          >
            <div className="flex items-center justify-between flex-wrap gap-4">
              <span className="flex items-center gap-2 font-medium text-dark-700">
                <Share2 className="w-5 h-5" />
                Condividi l'articolo
              </span>
              <div className="flex items-center gap-3">
                <a
                  href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-dark-100 flex items-center justify-center hover:bg-blue-600 hover:text-white transition-colors"
                >
                  <Facebook className="w-5 h-5" />
                </a>
                <a
                  href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(post.title)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-dark-100 flex items-center justify-center hover:bg-sky-500 hover:text-white transition-colors"
                >
                  <Twitter className="w-5 h-5" />
                </a>
                <a
                  href={`https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(shareUrl)}&title=${encodeURIComponent(post.title)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-dark-100 flex items-center justify-center hover:bg-blue-700 hover:text-white transition-colors"
                >
                  <Linkedin className="w-5 h-5" />
                </a>
              </div>
            </div>
          </motion.div>

          {/* CTA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-primary/10 rounded-2xl p-8 text-center"
          >
            <h3 className="text-2xl font-display font-bold mb-4">
              Vuoi risparmiare sulle tue bollette?
            </h3>
            <p className="text-dark-600 mb-6">
              Confronta gratuitamente le migliori offerte e inizia a risparmiare oggi stesso!
            </p>
            <a
              href="https://compare.bollettaamica.com/home/energia"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary"
            >
              Confronta le Offerte
            </a>
          </motion.div>
        </div>
      </article>
    </div>
  )
}
